import {
  Shield,
  Eye,
  Lock,
  Database,
  Globe,
  Mail,
  Calendar,
  AlertTriangle,
  Info,
  CheckCircle,
} from "lucide-react";
import "./Privacy.css";

const Privacy = () => {
  const lastUpdated = "December 15, 2024";

  return (
    <div className="privacy-page">
      <div className="privacy-header">
        <div className="header-content">
          <div className="header-icon">
            <Shield size={48} />
          </div>
          <div className="header-text">
            <h1>Privacy Policy</h1>
            <p>
              Your privacy is important to us. Learn how we collect, use, and
              protect your information.
            </p>
            <div className="last-updated">
              <Calendar size={16} />
              <span>Last updated: {lastUpdated}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="privacy-content">
        <div className="privacy-nav">
          <h3>Quick Navigation</h3>
          <ul>
            <li>
              <a href="#information-collection">Information We Collect</a>
            </li>
            <li>
              <a href="#information-use">How We Use Information</a>
            </li>
            <li>
              <a href="#information-sharing">Information Sharing</a>
            </li>
            <li>
              <a href="#data-security">Data Security</a>
            </li>
            <li>
              <a href="#cookies">Cookies & Tracking</a>
            </li>
            <li>
              <a href="#your-rights">Your Rights</a>
            </li>
            <li>
              <a href="#children-privacy">Children's Privacy</a>
            </li>
            <li>
              <a href="#international-transfers">International Transfers</a>
            </li>
            <li>
              <a href="#policy-changes">Policy Changes</a>
            </li>
            <li>
              <a href="#contact">Contact Us</a>
            </li>
          </ul>
        </div>

        <div className="privacy-sections">
          {/* Introduction */}
          <section className="privacy-section">
            <div className="section-icon">
              <Info size={24} />
            </div>
            <div className="section-content">
              <h2>Introduction</h2>
              <p>
                Welcome to ProSkillNest ("we," "our," or "us"). This Privacy
                Policy explains how we collect, use, disclose, and safeguard
                your information when you visit our website and use our
                services. Please read this privacy policy carefully. If you do
                not agree with the terms of this privacy policy, please do not
                access the site.
              </p>
              <div className="highlight-box">
                <CheckCircle size={20} />
                <div>
                  <strong>Our Commitment:</strong> We are committed to
                  protecting your privacy and ensuring the security of your
                  personal information.
                </div>
              </div>
            </div>
          </section>

          {/* Information Collection */}
          <section id="information-collection" className="privacy-section">
            <div className="section-icon">
              <Database size={24} />
            </div>
            <div className="section-content">
              <h2>Information We Collect</h2>

              <h3>Personal Information</h3>
              <p>
                We may collect personal information that you voluntarily provide
                to us when you:
              </p>
              <ul>
                <li>Register for an account</li>
                <li>Complete assessments or learning activities</li>
                <li>Contact us for support</li>
                <li>Subscribe to our newsletter</li>
                <li>Participate in surveys or feedback forms</li>
              </ul>

              <div className="info-types">
                <div className="info-type">
                  <h4>Account Information</h4>
                  <ul>
                    <li>Name and email address</li>
                    <li>Profile information (bio, location, website)</li>
                    <li>Profile picture or avatar</li>
                    <li>Learning preferences and goals</li>
                  </ul>
                </div>

                <div className="info-type">
                  <h4>Learning Data</h4>
                  <ul>
                    <li>Assessment scores and progress</li>
                    <li>Learning path enrollment and completion</li>
                    <li>Time spent on activities</li>
                    <li>Skill development metrics</li>
                  </ul>
                </div>

                <div className="info-type">
                  <h4>Technical Information</h4>
                  <ul>
                    <li>IP address and device information</li>
                    <li>Browser type and version</li>
                    <li>Operating system</li>
                    <li>Usage patterns and analytics</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Information Use */}
          <section id="information-use" className="privacy-section">
            <div className="section-icon">
              <Eye size={24} />
            </div>
            <div className="section-content">
              <h2>How We Use Your Information</h2>
              <p>
                We use the information we collect for various purposes,
                including:
              </p>

              <div className="use-cases">
                <div className="use-case">
                  <h4>Service Provision</h4>
                  <ul>
                    <li>Provide and maintain our learning platform</li>
                    <li>Process assessments and track progress</li>
                    <li>Personalize learning recommendations</li>
                    <li>Generate progress reports and analytics</li>
                  </ul>
                </div>

                <div className="use-case">
                  <h4>Communication</h4>
                  <ul>
                    <li>Send account-related notifications</li>
                    <li>Provide customer support</li>
                    <li>Send educational content and updates</li>
                    <li>Respond to inquiries and feedback</li>
                  </ul>
                </div>

                <div className="use-case">
                  <h4>Improvement & Analytics</h4>
                  <ul>
                    <li>Analyze usage patterns to improve our services</li>
                    <li>Conduct research and development</li>
                    <li>Monitor and analyze trends</li>
                    <li>Ensure platform security and prevent fraud</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Information Sharing */}
          <section id="information-sharing" className="privacy-section">
            <div className="section-icon">
              <Globe size={24} />
            </div>
            <div className="section-content">
              <h2>Information Sharing and Disclosure</h2>
              <p>
                We do not sell, trade, or rent your personal information to
                third parties. We may share your information in the following
                circumstances:
              </p>

              <div className="sharing-scenarios">
                <div className="scenario">
                  <h4>Service Providers</h4>
                  <p>
                    We may share information with trusted third-party service
                    providers who assist us in operating our platform, such as
                    hosting, analytics, and customer support services.
                  </p>
                </div>

                <div className="scenario">
                  <h4>Legal Requirements</h4>
                  <p>
                    We may disclose information if required by law, court order,
                    or government regulation, or to protect our rights,
                    property, or safety.
                  </p>
                </div>

                <div className="scenario">
                  <h4>Business Transfers</h4>
                  <p>
                    In the event of a merger, acquisition, or sale of assets,
                    your information may be transferred as part of the business
                    transaction.
                  </p>
                </div>

                <div className="scenario">
                  <h4>Consent</h4>
                  <p>
                    We may share information with your explicit consent or at
                    your direction.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Data Security */}
          <section id="data-security" className="privacy-section">
            <div className="section-icon">
              <Lock size={24} />
            </div>
            <div className="section-content">
              <h2>Data Security</h2>
              <p>
                We implement appropriate technical and organizational measures
                to protect your personal information:
              </p>

              <div className="security-measures">
                <div className="measure">
                  <h4>Encryption</h4>
                  <p>
                    Data is encrypted in transit and at rest using
                    industry-standard encryption protocols.
                  </p>
                </div>

                <div className="measure">
                  <h4>Access Controls</h4>
                  <p>
                    Strict access controls ensure only authorized personnel can
                    access personal information.
                  </p>
                </div>

                <div className="measure">
                  <h4>Regular Audits</h4>
                  <p>
                    We conduct regular security audits and vulnerability
                    assessments.
                  </p>
                </div>

                <div className="measure">
                  <h4>Secure Infrastructure</h4>
                  <p>
                    Our systems are hosted on secure, monitored infrastructure
                    with regular backups.
                  </p>
                </div>
              </div>

              <div className="security-notice">
                <AlertTriangle size={20} />
                <div>
                  <strong>Important:</strong> While we strive to protect your
                  information, no method of transmission over the internet or
                  electronic storage is 100% secure. We cannot guarantee
                  absolute security.
                </div>
              </div>
            </div>
          </section>

          {/* Cookies */}
          <section id="cookies" className="privacy-section">
            <div className="section-icon">
              <Globe size={24} />
            </div>
            <div className="section-content">
              <h2>Cookies and Tracking Technologies</h2>
              <p>
                We use cookies and similar tracking technologies to enhance your
                experience on our platform:
              </p>

              <div className="cookie-types">
                <div className="cookie-type">
                  <h4>Essential Cookies</h4>
                  <p>
                    Required for the platform to function properly, including
                    authentication and security features.
                  </p>
                </div>

                <div className="cookie-type">
                  <h4>Performance Cookies</h4>
                  <p>
                    Help us understand how users interact with our platform to
                    improve performance and user experience.
                  </p>
                </div>

                <div className="cookie-type">
                  <h4>Functional Cookies</h4>
                  <p>
                    Remember your preferences and settings to provide a
                    personalized experience.
                  </p>
                </div>

                <div className="cookie-type">
                  <h4>Analytics Cookies</h4>
                  <p>
                    Collect information about usage patterns to help us improve
                    our services.
                  </p>
                </div>
              </div>

              <p>
                You can control cookie preferences through your browser settings
                or our cookie consent banner.
              </p>
            </div>
          </section>

          {/* Your Rights */}
          <section id="your-rights" className="privacy-section">
            <div className="section-icon">
              <CheckCircle size={24} />
            </div>
            <div className="section-content">
              <h2>Your Privacy Rights</h2>
              <p>
                Depending on your location, you may have the following rights
                regarding your personal information:
              </p>

              <div className="rights-list">
                <div className="right">
                  <h4>Access</h4>
                  <p>
                    Request access to the personal information we hold about
                    you.
                  </p>
                </div>

                <div className="right">
                  <h4>Correction</h4>
                  <p>
                    Request correction of inaccurate or incomplete personal
                    information.
                  </p>
                </div>

                <div className="right">
                  <h4>Deletion</h4>
                  <p>
                    Request deletion of your personal information, subject to
                    certain exceptions.
                  </p>
                </div>

                <div className="right">
                  <h4>Portability</h4>
                  <p>
                    Request a copy of your personal information in a structured,
                    machine-readable format.
                  </p>
                </div>

                <div className="right">
                  <h4>Objection</h4>
                  <p>
                    Object to the processing of your personal information for
                    certain purposes.
                  </p>
                </div>

                <div className="right">
                  <h4>Restriction</h4>
                  <p>
                    Request restriction of processing under certain
                    circumstances.
                  </p>
                </div>
              </div>

              <p>
                To exercise these rights, please contact us using the
                information provided in the "Contact Us" section.
              </p>
            </div>
          </section>

          {/* Children's Privacy */}
          <section id="children-privacy" className="privacy-section">
            <div className="section-icon">
              <Shield size={24} />
            </div>
            <div className="section-content">
              <h2>Children's Privacy</h2>
              <p>
                Our services are not intended for children under the age of 13.
                We do not knowingly collect personal information from children
                under 13. If we become aware that we have collected personal
                information from a child under 13, we will take steps to delete
                such information.
              </p>
              <p>
                If you are a parent or guardian and believe your child has
                provided us with personal information, please contact us
                immediately.
              </p>
            </div>
          </section>

          {/* International Transfers */}
          <section id="international-transfers" className="privacy-section">
            <div className="section-icon">
              <Globe size={24} />
            </div>
            <div className="section-content">
              <h2>International Data Transfers</h2>
              <p>
                Your information may be transferred to and processed in
                countries other than your own. We ensure that such transfers are
                conducted in accordance with applicable data protection laws and
                that appropriate safeguards are in place to protect your
                information.
              </p>
            </div>
          </section>

          {/* Policy Changes */}
          <section id="policy-changes" className="privacy-section">
            <div className="section-icon">
              <Calendar size={24} />
            </div>
            <div className="section-content">
              <h2>Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will
                notify you of any material changes by posting the new Privacy
                Policy on this page and updating the "Last Updated" date. We
                encourage you to review this Privacy Policy periodically.
              </p>
            </div>
          </section>

          {/* Contact */}
          <section id="contact" className="privacy-section">
            <div className="section-icon">
              <Mail size={24} />
            </div>
            <div className="section-content">
              <h2>Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy or our
                privacy practices, please contact us:
              </p>

              <div className="contact-info">
                <div className="contact-method">
                  <Mail size={20} />
                  <div>
                    <strong>Email:</strong>
                    <a href="mailto:privacy@proskillnest.com">
                      privacy@proskillnest.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
